import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, switchMap, catchError, defer, EMPTY } from 'rxjs';

/** Success/error handlers for a wired pipeline. */
export interface FlowHandlers<T> {
  next: (value: T) => void;
  /** Called on error; the pipeline swallows the error and stays alive for the next trigger. */
  error: (err: unknown) => void;
}

/**
 * WalletDialogFlow
 *
 * Composition helper shared by the topup / send / withdrawal dialog stores. It
 * owns the *plumbing* of the cancellable request pipelines those stores
 * previously duplicated verbatim (methods, tokens, add-token): a trigger →
 * `switchMap` (auto-cancel the previous in-flight request) → `catchError`
 * (swallow, keep the stream alive) → `takeUntilDestroyed` → `subscribe` chain.
 *
 * It deliberately owns NO state signals. Each store keeps its own
 * loading/error/list signals and mutates them through the `next`/`error`
 * handlers — and encapsulates any pre-request guard in its own `project`
 * closure. This preserves every store's exact public API and behavior (e.g.
 * withdrawal routing method/authTxn errors into a single `error` signal;
 * send aborting the add-token flow silently when a method has no processor).
 *
 * authTxn, submit, detail/status, PIN, balance and dialog-step logic stay in the
 * concrete stores — only the identical list-fetch scaffolding is centralised.
 */
export class WalletDialogFlow {
  constructor(private readonly destroyRef: DestroyRef) {}

  /**
   * Wire a trigger to a cancellable request pipeline.
   *
   * @param trigger$ - Emits the input for each request (e.g. amount, processorId, form).
   * @param project  - Builds the request observable for a given input. May short-circuit
   *                   (return `EMPTY`, optionally after setting a store guard signal) — the
   *                   store owns all pre-request validation.
   * @param handlers - `next` on success; `error` when the request fails.
   */
  wire<TIn, TOut>(
    trigger$: Observable<TIn>,
    project: (input: TIn) => Observable<TOut>,
    handlers: FlowHandlers<TOut>,
  ): void {
    trigger$
      .pipe(
        switchMap((input) =>
          // `defer` so a SYNCHRONOUS throw from `project` (e.g. an API helper reading
          // credentials before building its request observable) becomes an error
          // notification this `catchError` can handle. Without it the throw escapes the
          // projection entirely — `.pipe(catchError(...))` is never even reached — and
          // lands on the outer stream, which has no error callback. That tears the
          // subscription down for the dialog's lifetime: the loading flag stays stuck,
          // no request is ever sent, and every retry is silently dead.
          defer(() => project(input)).pipe(
            catchError((err: unknown) => {
              handlers.error(err);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => handlers.next(value));
  }
}

/** Factory for a fresh, per-dialog {@link WalletDialogFlow}. */
export function createWalletDialogFlow(destroyRef: DestroyRef): WalletDialogFlow {
  return new WalletDialogFlow(destroyRef);
}
